import React, {FC} from 'react';
import styled from 'styled-components';

import {CrmSyncIntegrationResourceTypesEnum} from '../../../../../../../../src/models/api/crmSyncModel';
import {openInBrowser} from '../../../../../../../../srcCommon/browser/openInBrowser';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../../../../../shared/ui/src/components/booleanInputs/checkBox';
import {InlineLinkButton} from '../../../../../../../shared/ui/src/components/buttons/inlineLinkButton';
import {TooltipCoordinator} from '../../../../../../../shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../../../shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../../shared/ui/src/theme/userThemeProvider';

const maxTooltipWidth = 240;
const syncLogicLink = 'https://help.front.com/t/83hbpb1/salesforce-accounts-sync#account-sync-logic';

/*
 * Types.
 */

export interface CrmSyncsSkipNameMatchTooltipProps {
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  shouldSkipNameMatch?: boolean;
  onCheckboxChange: HandlerOf<boolean>;
}

/*
 * Style.
 */

const StyledCheckboxTooltipDiv = styled.div`
  display: flex;
  padding-top: 4px;
  padding-bottom: 30px;
`;

const StyledIconDiv = styled.div`
  display: flex;
  align-items: center;
  padding-left: 6px;
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledTooltipDiv = styled.div`
  padding: 4px;
`;

const StyledButtonTextSpan = styled.span`
  text-decoration: underline;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const CrmSyncsSkipNameMatchTooltip: FC<CrmSyncsSkipNameMatchTooltipProps> = (props) => {
  const {shouldSkipNameMatch, onCheckboxChange, resourceType} = props;
  const theme = useUserTheme();

  // Skipping name matching is only available for Account Sync for now.
  if (resourceType !== CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return null;
  }

  return (
    <StyledCheckboxTooltipDiv>
      <CheckBox isChecked={shouldSkipNameMatch} onChange={onCheckboxChange}>
        <Trans id="crm-syncs-page-name-matching">Allow duplicate names for accounts</Trans>
      </CheckBox>
      <StyledIconDiv>
        <TooltipCoordinator
          render={() => renderTooltip(theme.greys.white)}
          placement="top"
          isMultiline
          maxWidth={maxTooltipWidth}
          persistent
        />
      </StyledIconDiv>
    </StyledCheckboxTooltipDiv>
  );
};

function renderTooltip(linkColor: string) {
  return (
    <div>
      <StyledTooltipDiv>
        <Trans id="crm-syncs-page-name-matching-tooltip-heading">
          Front merges duplicative accounts based on domain and then name.
        </Trans>
      </StyledTooltipDiv>
      <StyledTooltipDiv>
        <Trans id="crm-syncs-page-name-matching-tooltip-description">
          By checking this box, Front will no longer merge accounts by name, and will allow multiple accounts
          with the same names.
        </Trans>
      </StyledTooltipDiv>
      <StyledTooltipDiv>
        <InlineLinkButton onClick={() => openInBrowser(syncLogicLink)} color={linkColor}>
          <StyledButtonTextSpan>
            <Trans id="crm-syncs-page-name-matching-tooltip-link">Learn more about sync logic</Trans>
          </StyledButtonTextSpan>
        </InlineLinkButton>
      </StyledTooltipDiv>
    </div>
  );
}
